// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useState } from 'react';

import { Accordion, Card, LoadingOverlay, Text, TextInput } from '@mantine/core';

import { AccordionControlsWithActions } from 'components/Mantine/AccordionControlsWithActions';
import { StatusPoint } from 'components/Mantine/StatusPoint';

import { AddEditStatusPopover } from './AddEditStatusPopover';
import { DeleteStatusModal } from './DeleteStatusModal';
import { StatusRow } from './StatusRow';
import type { CustomStatus, CustomStatusEntityName, EntityState, StateIdFor } from './types';
import { useCustomStatuses } from './useCustomStatuses';

type Props<TEntity extends CustomStatusEntityName> = {
  customStatusTypeName: TEntity;
};

type AccordionPanelProps<TEntity extends CustomStatusEntityName> = {
  customStatusTypeName: TEntity;
  state: EntityState<TEntity>;
  matchingStatuses: CustomStatus<TEntity>[];
  onDeleteStatus: (status: CustomStatus<TEntity>) => void;
};

const StateAccordion = <TEntity extends CustomStatusEntityName>({
  customStatusTypeName,
  state,
  matchingStatuses,
  onDeleteStatus,
}: AccordionPanelProps<TEntity>) => {
  const { stateColorMap } = useCustomStatuses(customStatusTypeName);
  const { label, color } = stateColorMap[state.name];

  return (
    <Accordion.Item value={state.stateId} key={state.stateId}>
      <AccordionControlsWithActions
        controlProps={{
          icon: <StatusPoint color={color} tooltip={label} />,
        }}
        rightSection={<AddEditStatusPopover customStatusTypeName={customStatusTypeName} stateId={state.stateId} />}
      >
        {label}
      </AccordionControlsWithActions>
      <Accordion.Panel>
        <Card style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
          {matchingStatuses.map((status) => (
            <Card.Section key={status.statusId as string} withBorder>
              <StatusRow customStatusTypeName={customStatusTypeName} status={status} onDeleteStatus={onDeleteStatus} />
            </Card.Section>
          ))}
        </Card>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

/**
 * Top-level custom-statuses accordion for the Admin company-properties page. Renders one
 * accordion item per canonical state (filtered to those that support custom statuses),
 * with searchable status filtering.
 */
export const CustomStatusesAccordion = <TEntity extends CustomStatusEntityName>({
  customStatusTypeName,
}: Props<TEntity>) => {
  const { states, statuses, loading, statesWithStatuses, stateColorMap } = useCustomStatuses(customStatusTypeName);

  const filteredStates = useMemo(
    () => states?.filter((state) => statesWithStatuses.includes(state.name)),
    [states, statesWithStatuses],
  );

  const [searchValue, setSearchValue] = useState('');
  const [deletingStatus, setDeletingStatus] = useState<CustomStatus<TEntity> | null>(null);

  const [accordionValue, setAccordionValue] = useState<StateIdFor<TEntity>[]>([]);
  useEffect(() => {
    setAccordionValue((current) =>
      current.length === 0 ? (filteredStates?.map((state) => state.stateId) ?? []) : current,
    );
  }, [filteredStates]);

  const searchLower = searchValue.toLowerCase();
  const visibleStates = useMemo<[state: EntityState<TEntity>, statuses: CustomStatus<TEntity>[]][]>(
    () =>
      (filteredStates ?? []).reduce<[EntityState<TEntity>, CustomStatus<TEntity>[]][]>((accumulator, state) => {
        const { label } = stateColorMap[state.name];
        // If the parent state name matches the search, show all its statuses; otherwise filter statuses.
        const stateNameMatches = label.toLowerCase().includes(searchLower);
        const matchingStatuses = stateNameMatches
          ? state.statuses
          : state.statuses.filter((status) => status.name.toLowerCase().includes(searchLower));
        if (matchingStatuses.length === 0) return accumulator;
        accumulator.push([state, matchingStatuses]);
        return accumulator;
      }, []),
    [filteredStates, searchLower, stateColorMap],
  );

  return (
    <>
      <TextInput
        placeholder="Search..."
        value={searchValue}
        onChange={(e) => setSearchValue(e.currentTarget.value)}
        w={300}
      />
      <Accordion
        styles={{
          control: { borderBottom: '1px solid var(--mantine-color-gray-3)' },
          item: { borderBottom: '0px' },
        }}
        multiple
        value={accordionValue}
        onChange={(value) => setAccordionValue(value as StateIdFor<TEntity>[])}
      >
        {visibleStates.map(([state, matchingStatuses]) => (
          <StateAccordion
            key={state.stateId}
            customStatusTypeName={customStatusTypeName}
            state={state}
            matchingStatuses={matchingStatuses}
            onDeleteStatus={setDeletingStatus}
          />
        ))}
      </Accordion>
      {loading ? (
        <Card withBorder mih={150} pos="relative">
          <LoadingOverlay visible />
        </Card>
      ) : (
        visibleStates.length === 0 && (
          <Card withBorder>
            <Text c="dimmed" fz="sm">
              {statuses.length === 0 ? 'You have no custom statuses.' : 'No matching statuses found.'}
            </Text>
          </Card>
        )
      )}
      <DeleteStatusModal
        customStatusTypeName={customStatusTypeName}
        status={deletingStatus}
        onClose={() => setDeletingStatus(null)}
      />
    </>
  );
};
